export const ExpelDialogContent = {
    title: 'Expel Student',
    dsescription: 'Are you sure you want to expel the following student from the class?',
    buttonName: 'Expel Student'
}

export const ExpelStudentScreenContent = {
    title: 'You have been removed from the class',
    description: 'You will not be able to rejoin this session',
    buttonName: 'Go to dashboard'
}

export const ButtonTexts = {
    CCSuggestionOutlined: 'Share Cue Cards in speak mode',
    CCSuggestionPrimary: 'Setup Breakout Rooms',
    StartBR: 'Start Breakout Rooms',
    Next: 'Next',
    Previous: 'Previous',
    ClearSelection: 'Clear all selection',
    ShareCC: 'Share Cue Cards',
    ApplyChanges: 'Apply Changes',
    RemoveCC: 'Remove Cue Cards',
    GoBack: 'Go Back',
    ExitBR: 'Exit Breakout Rooms',
    SaveChanges: 'Save Changes',
    Cancel: 'Cancel',
    ChooseBackground: 'Choose Background',
    ResetBackground: 'Reset Background'
}

export const DialogContent = {
    CCSuggestion: {
        title: 'Share Cue Cards in Breakout Rooms',
        body: 'Since there are more than three students in the class, it is recommended to share the Cue Card in Breakout Rooms.'
    },
    BreakoutRoom: {
        stepOneTitle: 'Breakout Rooms',
        stepTwoTitle: 'Share Cue Cards in Breakout Rooms'
    },
    CueCards: {
        title: 'Share Cue Cards'
    },
    RemoveCC: {
        title: 'Remove Cue Cards',
        body: 'Moving to another section will remove the cue cards for all students. Do you want to remove cue cards and continue?'
    },
    ExitBR: {
        title: 'Exit Breakout Rooms',
        body: 'You are about to end the breakout rooms and return to class mode. Do you want to proceed and end breakout rooms for all students?'
    }
}

export const AlertDialogContent = {
    TeacherTitle: 'Please ensure your camera is turned on before you join the class',
    StudentInfo: `We encourage you to turn on your camera to foster better communication and participation. 
        If you experience connection issues, please feel free to turn off your camera at any time.`,
    DiagnosticWarningTitle: 'Unable to access device',
    DiagnosticWarningDescription: 'Enable camera and microphone access to join the class. Click the lock icon in your browser to check permissions'
}

export const ClassEndWarningContent = {
    message: (countdown: string) => `The class is going to end in ${countdown} minutes`
}

export const PLATFORM_WEB = 'web'

export const FeedbackType = {
    POSITIVE: 'positive',
    NEGATIVE: 'negative'
} as const

// Mapping API feedbackType to FeedbackType constants
// feedbackType 0 = Why didn't the class go well? (NEGATIVE)
// feedbackType 1 = What did you like most? (POSITIVE)
export const FeedbackTypeMap = {
    0: FeedbackType.NEGATIVE,
    1: FeedbackType.POSITIVE
} as const

export const LEVEL_COMPLETION_FEATURE_FLAG = 'goc-level-completion-thank-you'
export const NEXT_STEP_URL = '/learn/dashboard?action=next-step'
/** Session storage key for Learn-app CTA label passed via Autologin `?nextStepLabel=` */
export const NEXT_STEP_LABEL_STORAGE_KEY = 'nextStepLabel'
/** Persisted on session join; thank-you screen reads it after conference disconnects */
export const UNIT_CODE_STORAGE_KEY = 'unitCode'

/** Encounter assessment result type id for "Continue" (matches SummaryPanel classResultEnum). */
export const ENCOUNTER_CLASS_RESULT_CONTINUE = 1

export const LevelCompletionVariant = {
    OFF: 0,
    CTA: 1,
    AUTO_REDIRECT: 2
} as const

export type LevelCompletionVariantValue =
    typeof LevelCompletionVariant[keyof typeof LevelCompletionVariant]

/** Query / GA labels for active level-completion variants */
export const LevelCompletionVariantLabel = {
    CTA: 'cta',
    AUTO_REDIRECT: 'auto_redirect'
} as const

export type LevelCompletionVariantLabelValue =
    typeof LevelCompletionVariantLabel[keyof typeof LevelCompletionVariantLabel]

export const SCREEN_VARIANT_QUERY_PARAM = 'screenVariant'

export const LEVEL_COMPLETION_COUNTDOWN_SECONDS = 10

export const parseLevelCompletionVariant = (value: unknown): LevelCompletionVariantValue => {
    const numeric = Number(value)
    if (numeric === LevelCompletionVariant.CTA || numeric === LevelCompletionVariant.AUTO_REDIRECT) {
        return numeric
    }
    return LevelCompletionVariant.OFF
}

export const getLevelCompletionVariantLabel = (
    variant: Exclude<LevelCompletionVariantValue, typeof LevelCompletionVariant.OFF>
): LevelCompletionVariantLabelValue =>
    variant === LevelCompletionVariant.CTA
        ? LevelCompletionVariantLabel.CTA
        : LevelCompletionVariantLabel.AUTO_REDIRECT

export const LevelCompletionScreenContent = {
    title: 'Well done!',
    subtitle: (unitNumber: number | string) =>
        `You have finished your Encounter ${unitNumber}! You are making great progress. Keep going!`,
    continueCta: 'Continue studying',
    stayOnPage: 'Stay on this page',
    countdownMessage: 'Your next step starts in ten seconds'
}

export const StudentThankYouScreenContent = {
    title: 'Thanks for joining 🙌',
    question: 'Did you enjoy today\'s class?',
    positiveFeedback: {
        label: 'I loved it',
        thankYou: 'Thanks for your feedback!'
    },
    negativeFeedback: {
        label: 'Not really',
        thankYou: 'Thanks for your feedback!'
    },
    backButton: 'Back',
    dashboardButton: 'Book my next class',
    submittedMessage: 'We appreciate your feedback 👏'
}

export const ASSESSMENT_RECOMMENDATIONS_FEATURE_FLAG = 'en-feedback-follow-up'

export const BookingStatus = {
    VALIDATED: 'validated'
} as const

export const AssessmentStatus = {
    WAITING_FOR_SESSION_JOIN: 'waiting-for-session-join',
    CLASS_DETAILS_LOADED: 'class-details-loaded',
    ENCOUNTER: 'encounter',
    NON_ENCOUNTER: 'non-encounter',
    STUDENT: 'student'
} as const

export type AssessmentStatusValue = typeof AssessmentStatus[keyof typeof AssessmentStatus]

export const StudentLevelInfoFetchState = {
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error'
} as const

export type StudentLevelInfoFetchStateValue = typeof StudentLevelInfoFetchState[keyof typeof StudentLevelInfoFetchState]
